import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────
interface PickItem {
  sku: string;
  product_name: string;
  product_id: number;
  total_quantity: number;
  orders: { order_number: string; quantity: number; order_id: number }[];
}
interface PickData {
  orders_count: number;
  unique_items: number;
  total_units: number;
  pick_list: PickItem[];
  orders_detail: { order_id: number; order_number: string; customer_name: string; customer_phone: string; status: string; items_count: number }[];
}
interface ShippingZone {
  id?: number;
  name: string;
  code: string;
  base_rate: number;
  per_kg_rate: number;
  base_weight_kg: number;
  volumetric_divisor: number;
  is_active?: boolean;
  display_order?: number;
}
interface WeightResult {
  actual_weight: number;
  volumetric_weight: number;
  chargeable_weight: number;
  dimensions: { length: number; width: number; height: number };
  volume_cm3: number;
  volumetric_divisor: number;
  shipping_cost: number | null;
  zone: { code: string; name: string; base_rate: number; per_kg_rate: number; base_weight_kg: number } | null;
  weight_type: 'actual' | 'volumetric';
  saved_to_order?: string;
}

const TABS = [
  { id: 'pickpack', label: 'Pick & Pack', icon: '📦' },
  { id: 'labels', label: 'Labels / AWB', icon: '🏷️' },
  { id: 'weight', label: 'Weight Calculator', icon: '⚖️' },
  { id: 'zones', label: 'Shipping Zones', icon: '🗺️' },
] as const;
type TabId = typeof TABS[number]['id'];

// ─────────────────────────────────────────────────────────────────────
const FulfillmentCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('pickpack');

  // ── Shared state: orders for pick/pack and labels ──
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);

  // ── Pick & Pack state ──
  const [pickData, setPickData] = useState<PickData | null>(null);
  const [pickLoading, setPickLoading] = useState(false);

  // ── Labels state ──
  const [labelLoading, setLabelLoading] = useState(false);

  // ── Weight Calculator state ──
  const [weightForm, setWeightForm] = useState({ actual_weight: '', length: '', width: '', height: '', order_id: '' });
  const [selectedZone, setSelectedZone] = useState('');
  const [weightResult, setWeightResult] = useState<WeightResult | null>(null);
  const [weightLoading, setWeightLoading] = useState(false);

  // ── Shipping Zones state ──
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null);
  const [zoneForm, setZoneForm] = useState<ShippingZone>({ name: '', code: '', base_rate: 0, per_kg_rate: 0, base_weight_kg: 1, volumetric_divisor: 5000 });
  const [zoneSaving, setZoneSaving] = useState(false);

  // ── Load orders on mount ──
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await adminAPI.getOrders();
      const data = res.data || [];
      // Only show fulfillable orders (pending, processing, no_response, shipped)
      const fulfillable = data.filter((o: any) => ['pending', 'no_response', 'processing', 'shipped'].includes(o.status));
      setOrders(fulfillable);
    } catch { /* ignore */ }
    setOrdersLoading(false);
  }, []);

  const loadZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const res = await adminAPI.getShippingZones();
      setZones(res.data || []);
    } catch { /* ignore */ }
    setZonesLoading(false);
  }, []);

  useEffect(() => { loadOrders(); loadZones(); }, [loadOrders, loadZones]);

  // ── Pick & Pack ──
  const handleGeneratePickList = async () => {
    if (selectedOrderIds.length === 0) return;
    setPickLoading(true);
    try {
      const res = await adminAPI.getPickPackList(selectedOrderIds);
      setPickData(res.data);
    } catch { setPickData(null); }
    setPickLoading(false);
  };

  const handlePrintPickList = async () => {
    if (selectedOrderIds.length === 0) return;
    setPickLoading(true);
    try {
      const res = await adminAPI.getPickPackPDF(selectedOrderIds);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e: any) {
      alert('Failed to generate PDF: ' + (e?.message || 'Unknown error'));
    }
    setPickLoading(false);
  };

  // ── Labels ──
  const handleGenerateLabels = async () => {
    if (selectedOrderIds.length === 0) return;
    setLabelLoading(true);
    try {
      const res = await adminAPI.generateLabels(selectedOrderIds);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e: any) {
      alert('Failed to generate labels: ' + (e?.message || 'Unknown error'));
    }
    setLabelLoading(false);
  };

  // ── Weight Calculator ──
  const handleCalculateWeight = async () => {
    setWeightLoading(true);
    try {
      const res = await adminAPI.calculateWeight({
        actual_weight: parseFloat(weightForm.actual_weight) || 0,
        length: parseFloat(weightForm.length) || 0,
        width: parseFloat(weightForm.width) || 0,
        height: parseFloat(weightForm.height) || 0,
        zone_code: selectedZone || undefined,
        order_id: weightForm.order_id ? parseInt(weightForm.order_id) : undefined,
      });
      setWeightResult(res.data);
    } catch { setWeightResult(null); }
    setWeightLoading(false);
  };

  // Live calculation
  const liveCalc = (() => {
    const aw = parseFloat(weightForm.actual_weight) || 0;
    const l = parseFloat(weightForm.length) || 0;
    const w = parseFloat(weightForm.width) || 0;
    const h = parseFloat(weightForm.height) || 0;
    const vol = l * w * h;
    const volWeight = vol / 5000;
    const chargeable = Math.max(aw, volWeight);
    return { volumetric: volWeight.toFixed(3), chargeable: chargeable.toFixed(3), volume: vol.toFixed(1), type: volWeight > aw ? 'volumetric' : 'actual' };
  })();

  // ── Shipping Zones CRUD ──
  const handleSaveZone = async () => {
    setZoneSaving(true);
    try {
      await adminAPI.saveShippingZone(editingZone ? { ...zoneForm, id: editingZone.id } : zoneForm);
      await loadZones();
      setEditingZone(null);
      setZoneForm({ name: '', code: '', base_rate: 0, per_kg_rate: 0, base_weight_kg: 1, volumetric_divisor: 5000 });
    } catch { /* ignore */ }
    setZoneSaving(false);
  };

  const handleDeleteZone = async (id: number) => {
    if (!window.confirm('Delete this shipping zone?')) return;
    try {
      await adminAPI.deleteShippingZone(id);
      await loadZones();
    } catch { /* ignore */ }
  };

  // ── Checkbox helpers ──
  const toggleOrder = (id: number) => {
    setSelectedOrderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAll = () => {
    if (selectedOrderIds.length === orders.length) setSelectedOrderIds([]);
    else setSelectedOrderIds(orders.map(o => o.id));
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin-page adm-page">
      {/* Page Header */}
      <div className="adm-topbar">
        <h1 className="adm-page-title">Fulfillment Center</h1>
      </div>

      {/* Tab Bar */}
      <div className="adm-tabs" style={{ marginBottom: '20px' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`adm-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════ PICK & PACK TAB ═══════════ */}
      {activeTab === 'pickpack' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={handleGeneratePickList} disabled={selectedOrderIds.length === 0 || pickLoading}
              style={{ padding: '9px 18px', background: selectedOrderIds.length === 0 ? '#d1d5db' : '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: selectedOrderIds.length === 0 ? 'not-allowed' : 'pointer' }}>
              {pickLoading ? '⏳ Generating...' : `📋 Generate Pick List (${selectedOrderIds.length})`}
            </button>
            <button onClick={handlePrintPickList} disabled={selectedOrderIds.length === 0 || pickLoading}
              style={{ padding: '9px 18px', background: selectedOrderIds.length === 0 ? '#d1d5db' : '#059669', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: selectedOrderIds.length === 0 ? 'not-allowed' : 'pointer' }}>
              🖨️ Print Pick List PDF
            </button>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              {selectedOrderIds.length} order(s) selected · {orders.length} fulfillable
            </span>
          </div>

          {/* Order selection table */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={thStyle}><input type="checkbox" checked={selectedOrderIds.length === orders.length && orders.length > 0} onChange={selectAll} /></th>
                    <th style={thStyle}>Order</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Items</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersLoading ? (
                    <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>Loading orders...</td></tr>
                  ) : orders.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>No fulfillable orders found</td></tr>
                  ) : orders.map(o => (
                    <tr key={o.id} style={{ borderBottom: '1px solid #f3f4f6', background: selectedOrderIds.includes(o.id) ? '#eef2ff' : 'white', cursor: 'pointer' }}
                      onClick={() => toggleOrder(o.id)}>
                      <td style={tdStyle}><input type="checkbox" checked={selectedOrderIds.includes(o.id)} onChange={() => toggleOrder(o.id)} onClick={e => e.stopPropagation()} /></td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{o.order_number}</td>
                      <td style={tdStyle}>{o.customer_name}</td>
                      <td style={tdStyle}>{o.customer_phone}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{o.items?.length || o.item_count || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>৳{o.final_amount?.toLocaleString()}</td>
                      <td style={tdStyle}>
                        <span style={{ background: statusColor(o.status).bg, color: statusColor(o.status).text, padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pick data result */}
          {pickData && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ background: '#4f46e5', color: 'white', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>📋 Pick List</h3>
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
                    {pickData.orders_count} orders · {pickData.unique_items} unique items · {pickData.total_units} total units
                  </p>
                </div>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>SKU</th>
                      <th style={thStyle}>Product Name</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Total Qty</th>
                      <th style={thStyle}>From Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickData.pick_list.map((item, i) => (
                      <tr key={item.product_id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...tdStyle, color: '#9ca3af', fontSize: '12px' }}>{i + 1}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '600', color: '#4f46e5' }}>{item.sku}</td>
                        <td style={{ ...tdStyle, fontWeight: '500' }}>{item.product_name}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '800', fontSize: '15px', color: '#111827' }}>{item.total_quantity}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {item.orders.map(o => (
                              <span key={o.order_number} style={{ background: '#eef2ff', color: '#4f46e5', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                                {o.order_number} ×{o.quantity}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ LABELS / AWB TAB ═══════════ */}
      {activeTab === 'labels' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
            <button onClick={handleGenerateLabels} disabled={selectedOrderIds.length === 0 || labelLoading}
              style={{ padding: '9px 18px', background: selectedOrderIds.length === 0 ? '#d1d5db' : '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: selectedOrderIds.length === 0 ? 'not-allowed' : 'pointer' }}>
              {labelLoading ? '⏳ Generating...' : `🏷️ Generate & Print Labels (${selectedOrderIds.length})`}
            </button>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Select orders below, then click to generate printable A5 shipping labels with barcodes & QR codes
            </span>
          </div>

          {/* Same order table */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={thStyle}><input type="checkbox" checked={selectedOrderIds.length === orders.length && orders.length > 0} onChange={selectAll} /></th>
                    <th style={thStyle}>Order</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Address</th>
                    <th style={thStyle}>COD</th>
                    <th style={thStyle}>Tracking</th>
                    <th style={thStyle}>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersLoading ? (
                    <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                  ) : orders.map(o => (
                    <tr key={o.id} style={{ borderBottom: '1px solid #f3f4f6', background: selectedOrderIds.includes(o.id) ? '#fef2f2' : 'white', cursor: 'pointer' }}
                      onClick={() => toggleOrder(o.id)}>
                      <td style={tdStyle}><input type="checkbox" checked={selectedOrderIds.includes(o.id)} onChange={() => toggleOrder(o.id)} onClick={e => e.stopPropagation()} /></td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{o.order_number}</td>
                      <td style={tdStyle}>{o.customer_name}<br /><span style={{ color: '#9ca3af', fontSize: '11px' }}>{o.customer_phone}</span></td>
                      <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.shipping_address}</td>
                      <td style={{ ...tdStyle, fontWeight: '700', color: '#dc2626' }}>৳{o.final_amount?.toLocaleString()}</td>
                      <td style={tdStyle}>
                        {o.steadfast_tracking_code
                          ? <span style={{ fontFamily: 'monospace', color: '#059669', fontWeight: '600' }}>{o.steadfast_tracking_code}</span>
                          : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={tdStyle}>{o.chargeable_weight ? `${o.chargeable_weight} kg` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Label preview info */}
          <div style={{ marginTop: '16px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px 18px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#92400e' }}>
              💡 <strong>Labels include:</strong> Sender/Receiver details, barcode (tracking code), QR code, order items, COD amount, weight.
              Each order generates one A5 label. Sender info is auto-filled from Website Settings.
            </p>
          </div>
        </div>
      )}

      {/* ═══════════ WEIGHT CALCULATOR TAB ═══════════ */}
      {activeTab === 'weight' && (
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {/* Input form */}
          <div style={{ flex: '1 1 340px', minWidth: '300px' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ background: '#f9fafb', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827' }}>⚖️ Package Details</h3>
              </div>
              <div style={{ padding: '18px' }}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Actual Weight (kg)</label>
                  <input type="number" step="0.001" placeholder="e.g. 2.5"
                    value={weightForm.actual_weight}
                    onChange={e => setWeightForm({ ...weightForm, actual_weight: e.target.value })}
                    style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Length (cm)</label>
                    <input type="number" step="0.1" placeholder="L"
                      value={weightForm.length}
                      onChange={e => setWeightForm({ ...weightForm, length: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Width (cm)</label>
                    <input type="number" step="0.1" placeholder="W"
                      value={weightForm.width}
                      onChange={e => setWeightForm({ ...weightForm, width: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Height (cm)</label>
                    <input type="number" step="0.1" placeholder="H"
                      value={weightForm.height}
                      onChange={e => setWeightForm({ ...weightForm, height: e.target.value })}
                      style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Shipping Zone (optional)</label>
                  <select value={selectedZone} onChange={e => setSelectedZone(e.target.value)} style={inputStyle}>
                    <option value="">— No zone (calculation only) —</option>
                    {zones.map(z => (
                      <option key={z.code} value={z.code}>{z.name} (৳{z.base_rate}+৳{z.per_kg_rate}/kg)</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Save to Order (optional)</label>
                  <input type="number" placeholder="Order ID"
                    value={weightForm.order_id}
                    onChange={e => setWeightForm({ ...weightForm, order_id: e.target.value })}
                    style={inputStyle} />
                </div>
                <button onClick={handleCalculateWeight} disabled={weightLoading}
                  style={{ width: '100%', padding: '10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer' }}>
                  {weightLoading ? '⏳ Calculating...' : '⚖️ Calculate & Save'}
                </button>
              </div>
            </div>
          </div>

          {/* Live result panel */}
          <div style={{ flex: '1 1 340px', minWidth: '300px' }}>
            {/* Live calculation */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ background: '#eef2ff', padding: '14px 18px', borderBottom: '1px solid #c7d2fe' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#4338ca' }}>📐 Live Calculation</h3>
              </div>
              <div style={{ padding: '18px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                  <MetricCard label="Volume" value={`${liveCalc.volume} cm³`} color="#6b7280" />
                  <MetricCard label="Volumetric Wt" value={`${liveCalc.volumetric} kg`} color="#7c3aed" />
                </div>
                <div style={{
                  background: liveCalc.type === 'volumetric' ? '#fef3c7' : '#d1fae5',
                  border: `1px solid ${liveCalc.type === 'volumetric' ? '#fde68a' : '#a7f3d0'}`,
                  borderRadius: '10px', padding: '14px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Chargeable Weight</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: '#111827' }}>{liveCalc.chargeable} kg</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    Based on <strong>{liveCalc.type}</strong> weight (higher of actual vs volumetric)
                  </div>
                </div>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                  Formula: (L × W × H) ÷ 5000 = Volumetric Weight
                </div>
              </div>
            </div>

            {/* Server result */}
            {weightResult && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ background: '#ecfdf5', padding: '14px 18px', borderBottom: '1px solid #a7f3d0' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#065f46' }}>✅ Calculation Result</h3>
                </div>
                <div style={{ padding: '18px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                    <MetricCard label="Actual Weight" value={`${weightResult.actual_weight} kg`} color="#111827" />
                    <MetricCard label="Volumetric Weight" value={`${weightResult.volumetric_weight} kg`} color="#7c3aed" />
                    <MetricCard label="Chargeable Weight" value={`${weightResult.chargeable_weight} kg`} color="#dc2626" highlight />
                    <MetricCard label="Volume" value={`${weightResult.volume_cm3} cm³`} color="#6b7280" />
                  </div>
                  {weightResult.shipping_cost !== null && weightResult.zone && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', fontWeight: '600', color: '#991b1b', textTransform: 'uppercase' }}>Shipping Cost ({weightResult.zone.name})</div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>৳{weightResult.shipping_cost?.toLocaleString()}</div>
                    </div>
                  )}
                  {weightResult.saved_to_order && (
                    <div style={{ marginTop: '10px', background: '#d1fae5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#065f46', fontWeight: '600' }}>
                      ✅ Saved to order: {weightResult.saved_to_order}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ SHIPPING ZONES TAB ═══════════ */}
      {activeTab === 'zones' && (
        <div>
          {/* Zone form */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ background: '#f9fafb', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>{editingZone ? '✏️ Edit Zone' : '➕ Add Shipping Zone'}</h3>
              {editingZone && (
                <button onClick={() => { setEditingZone(null); setZoneForm({ name: '', code: '', base_rate: 0, per_kg_rate: 0, base_weight_kg: 1, volumetric_divisor: 5000 }); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
              )}
            </div>
            <div style={{ padding: '18px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 150px' }}>
                <label style={labelStyle}>Zone Name</label>
                <input placeholder="e.g. Dhaka Metro" value={zoneForm.name} onChange={e => setZoneForm({ ...zoneForm, name: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ flex: '0 0 120px' }}>
                <label style={labelStyle}>Code</label>
                <input placeholder="e.g. dhaka" value={zoneForm.code} onChange={e => setZoneForm({ ...zoneForm, code: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <label style={labelStyle}>Base Rate (৳)</label>
                <input type="number" value={zoneForm.base_rate} onChange={e => setZoneForm({ ...zoneForm, base_rate: parseFloat(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <label style={labelStyle}>Per KG (৳)</label>
                <input type="number" value={zoneForm.per_kg_rate} onChange={e => setZoneForm({ ...zoneForm, per_kg_rate: parseFloat(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <label style={labelStyle}>Base Weight (kg)</label>
                <input type="number" step="0.5" value={zoneForm.base_weight_kg} onChange={e => setZoneForm({ ...zoneForm, base_weight_kg: parseFloat(e.target.value) || 1 })} style={inputStyle} />
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <label style={labelStyle}>Vol. Divisor</label>
                <input type="number" value={zoneForm.volumetric_divisor} onChange={e => setZoneForm({ ...zoneForm, volumetric_divisor: parseInt(e.target.value) || 5000 })} style={inputStyle} />
              </div>
              <button onClick={handleSaveZone} disabled={zoneSaving || !zoneForm.name || !zoneForm.code}
                style={{ padding: '9px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer', height: '40px' }}>
                {zoneSaving ? '⏳' : editingZone ? '💾 Update' : '➕ Add'}
              </button>
            </div>
          </div>

          {/* Zones table */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={thStyle}>Zone Name</th>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Base Rate</th>
                  <th style={thStyle}>Per KG Rate</th>
                  <th style={thStyle}>Base Weight</th>
                  <th style={thStyle}>Divisor</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {zonesLoading ? (
                  <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                ) : zones.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>No shipping zones yet. Add one above.</td></tr>
                ) : zones.map(z => (
                  <tr key={z.id || z.code} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{z.name}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#4f46e5' }}>{z.code}</td>
                    <td style={tdStyle}>৳{Number(z.base_rate).toLocaleString()}</td>
                    <td style={tdStyle}>৳{Number(z.per_kg_rate).toLocaleString()}/kg</td>
                    <td style={tdStyle}>{z.base_weight_kg} kg</td>
                    <td style={tdStyle}>{z.volumetric_divisor}</td>
                    <td style={tdStyle}>
                      <button onClick={() => { setEditingZone(z as any); setZoneForm(z as any); }}
                        style={{ background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginRight: '6px' }}>
                        Edit
                      </button>
                      <button onClick={() => z.id && handleDeleteZone(z.id)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Helper Components & Styles
// ─────────────────────────────────────────────────────────────────────
const MetricCard: React.FC<{ label: string; value: string; color: string; highlight?: boolean }> = ({ label, value, color, highlight }) => (
  <div style={{
    background: highlight ? '#fef2f2' : '#f9fafb',
    border: `1px solid ${highlight ? '#fecaca' : '#e5e7eb'}`,
    borderRadius: '8px', padding: '10px', textAlign: 'center',
  }}>
    <div style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    <div style={{ fontSize: '16px', fontWeight: '800', color }}>{value}</div>
  </div>
);

const thStyle: React.CSSProperties = {
  padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151',
  borderBottom: '1px solid #e5e7eb', fontSize: '12px',
};
const tdStyle: React.CSSProperties = {
  padding: '9px 12px', color: '#374151',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px',
  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
};

function statusColor(status: string) {
  switch (status) {
    case 'pending': return { bg: '#fef3c7', text: '#92400e' };
    case 'processing': return { bg: '#dbeafe', text: '#1e40af' };
    case 'shipped': return { bg: '#d1fae5', text: '#065f46' };
    case 'no_response': return { bg: '#fee2e2', text: '#991b1b' };
    default: return { bg: '#f3f4f6', text: '#6b7280' };
  }
}

export default FulfillmentCenter;
